'use client'

/**
 * IdvActivitySankey - Pure visualization component for IDV hierarchy
 *
 * Renders a Sankey diagram showing money flow from parent IDV to child awards
 * and grandchild awards. Pure/idempotent - same props always produce same output.
 *
 * Features:
 * - Interactive nodes with click navigation to award details
 * - Custom link layer with wider hit areas for reliable hover
 * - Rich hover tooltips with detailed contract information
 * - Color coding by award type with smooth animations
 * - Multiple grouping modes (Individual, Category, Type, Recipient)
 * - Responsive to container width
 */
import { useCallback, useMemo, useState } from 'react'

import type { SankeyLinkDatum, SankeyNodeDatum } from '@nivo/sankey'
import { ResponsiveSankey } from '@nivo/sankey'
import { useTheme } from 'next-themes'
import { useRouter } from 'next/navigation'

import { APP_THEME } from '@/constants/theme'

import type {
  ApiIdvActivityLink,
  ApiIdvActivityNode,
  ApiIdvActivityResponse,
} from '@/context/usaspending'

import { useMounted } from '@/hooks/common/useMounted'

import { formatCompactCurrency, formatDate } from '../../common/transforms'

// ============================================================================
// Types
// ============================================================================

/** Extended node with full award details */
interface SankeyNode {
  id: string
  nodeColor: string
  label: string
  piid: string | null
  category: string
  type: string | null
  typeDescription: string | null
  recipientName: string | null
  recipientHash: string | null
  awardingAgency: string | null
  totalObligation: number | null
  actionDate: string | null
  level: number
  isGrandchild: boolean
  /** For missing data nodes only */
  missingCount?: number
}

/** Nivo Sankey link format */
interface SankeyLink {
  source: string
  target: string
  value: number
}

/** Sankey data format for @nivo/sankey */
interface SankeyData {
  nodes: SankeyNode[]
  links: SankeyLink[]
}

/** Grouping modes for the Sankey visualization */
type GroupingMode =
  | 'individual'
  | 'hierarchy'
  | 'category'
  | 'type'
  | 'recipient'

interface GroupingOption {
  value: GroupingMode
  label: string
  description: string
}

const GROUPING_OPTIONS: GroupingOption[] = [
  {
    value: 'hierarchy',
    label: 'Hierarchy',
    description: 'IDV → Category → Individual items',
  },
  {
    value: 'individual',
    label: 'Individual',
    description: 'Each award shown separately',
  },
  {
    value: 'category',
    label: 'Category',
    description: 'Aggregate IDVs and Contracts',
  },
  {
    value: 'type',
    label: 'Award Type',
    description: 'Group by BPA, DO, TO, etc.',
  },
  {
    value: 'recipient',
    label: 'Recipient',
    description: 'Group by recipient organization',
  },
]

interface IdvActivitySankeyProps {
  /** Activity data from API */
  data: ApiIdvActivityResponse
  /** Parent award PIID for display */
  parentPiid: string
  /** Optional callback when a node is clicked (for custom handling) */
  onNodeClick?: (nodeId: string) => void
  /** Height of the Sankey diagram */
  height?: number
  /** Whether to enable navigation on click (default: true) */
  enableNavigation?: boolean
  /** Controlled grouping mode */
  grouping?: GroupingMode
  /** Default grouping for uncontrolled mode */
  defaultGrouping?: GroupingMode
  /** Callback when grouping changes */
  onGroupingChange?: (mode: GroupingMode) => void
  /** Whether to show grouping controls (default: true) */
  showGroupingControls?: boolean
}

// ============================================================================
// Pure Transform Functions
// ============================================================================

/** Category color palette - Blues & Teals for professional look */
const CATEGORY_COLORS = {
  idv: '#1e40af', // Navy blue for IDVs (parent vehicles)
  contract: '#0891b2', // Cyan for contracts
  grant: '#0ea5e9', // Sky blue for grants
  pending: '#9ca3af', // Gray for pending/missing data
  unknown: '#6b7280', // Gray for unknown types
} as const

/**
 * Get color for award category - pure function
 */
function getCategoryColor(category: string): string {
  const key = category.toLowerCase() as keyof typeof CATEGORY_COLORS
  return CATEGORY_COLORS[key] ?? CATEGORY_COLORS.unknown
}

/**
 * Get label for category badge - pure function
 */
function getCategoryLabel(category: string, isGrandchild: boolean): string {
  if (isGrandchild) return 'Order (Grandchild)'
  switch (category.toLowerCase()) {
    case 'idv':
      return 'IDV'
    case 'contract':
      return 'Contract Order'
    case 'grant':
      return 'Grant'
    case 'pending':
      return 'Pending Data'
    default:
      return category.toUpperCase()
  }
}

function formatCompactCurrencyOrUnknown(
  value: number | null | undefined,
): string {
  return typeof value === 'number'
    ? (formatCompactCurrency(value) ?? 'Unknown')
    : 'Unknown'
}

/**
 * Transform API nodes to Sankey format - pure function
 */
function transformNodesToSankey(
  apiNodes: ApiIdvActivityNode[],
  parentUniqueId: string,
  parentPiid: string,
  parentObligation: number,
): SankeyNode[] {
  const parentNode: SankeyNode = {
    id: parentUniqueId,
    nodeColor: getCategoryColor('idv'),
    label: parentPiid,
    piid: parentPiid,
    category: 'idv',
    type: null,
    typeDescription: 'Indefinite Delivery Vehicle',
    recipientName: null,
    recipientHash: null,
    awardingAgency: null,
    totalObligation: parentObligation,
    actionDate: null,
    level: 0,
    isGrandchild: false,
  }

  const childNodes: SankeyNode[] = apiNodes.map((node) => ({
    id: node.id,
    nodeColor: getCategoryColor(node.category),
    label: node.piid ?? node.id.slice(-8),
    piid: node.piid,
    category: node.category,
    type: node.type,
    typeDescription: node.type_description,
    recipientName: node.recipient_name,
    recipientHash: node.recipient_hash,
    awardingAgency: node.awarding_agency,
    totalObligation: node.total_obligation,
    actionDate: node.action_date,
    level: node.level,
    isGrandchild: node.is_grandchild,
  }))

  return [parentNode, ...childNodes]
}

/**
 * Transform API links to Sankey format - pure function
 */
function transformLinksToSankey(apiLinks: ApiIdvActivityLink[]): SankeyLink[] {
  return apiLinks
    .filter(
      (link): link is ApiIdvActivityLink & { value: number } =>
        typeof link.value === 'number' && link.value > 0,
    )
    .map((link) => ({
      source: link.source,
      target: link.target,
      value: link.value,
    }))
}

/**
 * Calculate missing data info from summary - pure function
 */
function getMissingDataInfo(summary: ApiIdvActivityResponse['summary']): {
  missingCount: number
  missingObligation: number
  hasSignificantMissing: boolean
  hasObligationMismatch: boolean
  obligationMismatchPercent: number
} {
  const expectedCount = summary.rollup_idv_count + summary.rollup_contract_count
  const missingCount = expectedCount - summary.actual_rollup_count
  const missingObligation =
    summary.rollup_total_obligation - summary.actual_total_obligation

  // Calculate obligation mismatch percentage (when we have all orders but amounts differ)
  const obligationMismatchPercent =
    summary.rollup_total_obligation > 0
      ? Math.abs(missingObligation / summary.rollup_total_obligation) * 100
      : 0

  // Show mismatch warning when: no missing count, but >5% obligation difference
  const hasObligationMismatch =
    missingCount <= 0 && obligationMismatchPercent > 5

  return {
    missingCount: Math.max(0, missingCount),
    missingObligation: Math.max(0, missingObligation),
    hasSignificantMissing: missingCount > 0 && missingObligation > 0,
    hasObligationMismatch,
    obligationMismatchPercent,
  }
}

/**
 * Transform API response to Sankey data format - pure function
 */
function transformToSankeyData(
  data: ApiIdvActivityResponse,
  parentPiid: string,
): SankeyData {
  const nodes = transformNodesToSankey(
    data.nodes,
    data.parent_unique_id,
    parentPiid,
    data.summary.rollup_total_obligation,
  )

  const links = transformLinksToSankey(data.links)

  const { missingCount, missingObligation, hasSignificantMissing } =
    getMissingDataInfo(data.summary)

  if (hasSignificantMissing) {
    const missingNodeId = '__pending__'
    nodes.push({
      id: missingNodeId,
      nodeColor: getCategoryColor('pending'),
      label: `+${missingCount} pending`,
      piid: null,
      category: 'pending',
      type: null,
      typeDescription: null,
      recipientName: null,
      recipientHash: null,
      awardingAgency: null,
      totalObligation: missingObligation,
      actionDate: null,
      level: 1,
      isGrandchild: false,
      missingCount,
    })

    links.push({
      source: data.parent_unique_id,
      target: missingNodeId,
      value: missingObligation,
    })
  }

  return { nodes, links }
}

/**
 * Build lookup map from node id to SankeyNode - pure function
 */
function buildNodeMap(nodes: SankeyNode[]): Map<string, SankeyNode> {
  return new Map(nodes.map((n) => [n.id, n]))
}

// ============================================================================
// Grouped Transform Functions
// ============================================================================

/** Type-specific colors for better visual distinction - Blues & Teals */
const TYPE_COLORS: Record<string, string> = {
  // IDV Types - Navy to Blue range
  BPA: '#2563eb', // Blue - Blanket Purchase Agreement
  'BLANKET PURCHASE AGREEMENT': '#2563eb',
  BOA: '#1d4ed8', // Blue darker - Basic Ordering Agreement
  'BASIC ORDERING AGREEMENT': '#1d4ed8',
  FSS: '#3b82f6', // Blue lighter - Federal Supply Schedule
  'FSS / NATIONAL STOCK NUMBER': '#3b82f6',
  GWAC: '#1e40af', // Navy - Government-wide Acquisition Contract
  'GOVERNMENT-WIDE ACQUISITION CONTRACT (GWAC)': '#1e40af',
  IDC: '#1e3a8a', // Navy darker - Indefinite Delivery Contract
  'INDEFINITE DELIVERY CONTRACT': '#1e3a8a',

  // Contract/Order Types - Cyan to Teal range
  DO: '#0891b2', // Cyan - Delivery Order
  'DELIVERY ORDER': '#0891b2',
  TO: '#06b6d4', // Cyan lighter - Task Order
  'TASK ORDER': '#06b6d4',
  PO: '#0e7490', // Cyan darker - Purchase Order
  'PURCHASE ORDER': '#0e7490',
  'DEFINITIVE CONTRACT': '#0284c7', // Sky blue
  'BPA CALL': '#22d3d1', // Teal light

  // Fallbacks
  IDV: '#1e40af',
  CONTRACT: '#0891b2',
}

function getTypeColor(type: string | null, category: string): string {
  if (!type) return getCategoryColor(category)
  const upperType = type.toUpperCase()
  return TYPE_COLORS[upperType] ?? getCategoryColor(category)
}

/**
 * Transform to grouped Sankey data by category (IDV vs Contract) - pure function
 * Uses flow value map to ensure nodes only appear when there's actual flow
 */
function transformToCategoryGrouped(
  data: ApiIdvActivityResponse,
  parentPiid: string,
): SankeyData {
  const parentId = data.parent_unique_id
  const flowValueMap = buildFlowValueMap(data.links)

  const getFlowValue = (nodeId: string, fallback?: number | null): number =>
    flowValueMap.get(nodeId) ?? fallback ?? 0

  const parentNode: SankeyNode = {
    id: parentId,
    nodeColor: getCategoryColor('idv'),
    label: parentPiid,
    piid: parentPiid,
    category: 'idv',
    type: null,
    typeDescription: 'Parent IDV',
    recipientName: null,
    recipientHash: null,
    awardingAgency: null,
    totalObligation: data.summary.rollup_total_obligation,
    actionDate: null,
    level: 0,
    isGrandchild: false,
  }

  const nodes: SankeyNode[] = [parentNode]
  const links: SankeyLink[] = []

  // Group nodes by category
  const idvNodes = data.nodes.filter((n) => n.category === 'idv')
  const contractNodes = data.nodes.filter((n) => n.category !== 'idv')

  // Calculate IDV total using flow values
  let idvTotal = 0
  for (const n of idvNodes) {
    idvTotal += getFlowValue(n.id, n.total_obligation)
  }

  // Only add IDV group if there's actual flow
  if (idvTotal > 0) {
    nodes.push({
      id: '__group_idv__',
      nodeColor: getCategoryColor('idv'),
      label: `Child IDVs (${idvNodes.length})`,
      piid: null,
      category: 'idv',
      type: null,
      typeDescription: `${idvNodes.length} Indefinite Delivery Vehicles`,
      recipientName: null,
      recipientHash: null,
      awardingAgency: null,
      totalObligation: idvTotal,
      actionDate: null,
      level: 1,
      isGrandchild: false,
    })
    links.push({
      source: parentId,
      target: '__group_idv__',
      value: idvTotal,
    })
  }

  // Process contracts
  const directContracts = contractNodes.filter((n) => !n.is_grandchild)
  const grandchildContracts = contractNodes.filter((n) => n.is_grandchild)

  // Calculate direct contract total using flow values
  let directTotal = 0
  for (const n of directContracts) {
    directTotal += getFlowValue(n.id, n.total_obligation)
  }

  // Only add direct contracts group if there's actual flow
  if (directTotal > 0) {
    nodes.push({
      id: '__group_contract_direct__',
      nodeColor: getCategoryColor('contract'),
      label: `Direct Orders (${directContracts.length})`,
      piid: null,
      category: 'contract',
      type: null,
      typeDescription: `${directContracts.length} contracts placed directly on this IDV`,
      recipientName: null,
      recipientHash: null,
      awardingAgency: null,
      totalObligation: directTotal,
      actionDate: null,
      level: 1,
      isGrandchild: false,
    })
    links.push({
      source: parentId,
      target: '__group_contract_direct__',
      value: directTotal,
    })
  }

  // Calculate grandchild total using flow values
  let grandchildTotal = 0
  for (const n of grandchildContracts) {
    grandchildTotal += getFlowValue(n.id, n.total_obligation)
  }

  // Only add grandchild group if there's actual flow AND IDV group exists
  if (grandchildTotal > 0 && idvTotal > 0) {
    nodes.push({
      id: '__group_contract_grandchild__',
      nodeColor: '#22d3d1',
      label: `Grandchild Orders (${grandchildContracts.length})`,
      piid: null,
      category: 'contract',
      type: null,
      typeDescription: `${grandchildContracts.length} contracts placed on child IDVs`,
      recipientName: null,
      recipientHash: null,
      awardingAgency: null,
      totalObligation: grandchildTotal,
      actionDate: null,
      level: 2,
      isGrandchild: true,
    })
    links.push({
      source: '__group_idv__',
      target: '__group_contract_grandchild__',
      value: grandchildTotal,
    })
  }

  // Add pending node if significant missing data
  const { missingCount, missingObligation, hasSignificantMissing } =
    getMissingDataInfo(data.summary)
  if (hasSignificantMissing) {
    nodes.push({
      id: '__pending__',
      nodeColor: getCategoryColor('pending'),
      label: `+${missingCount} pending`,
      piid: null,
      category: 'pending',
      type: null,
      typeDescription: null,
      recipientName: null,
      recipientHash: null,
      awardingAgency: null,
      totalObligation: missingObligation,
      actionDate: null,
      level: 1,
      isGrandchild: false,
      missingCount,
    })
    links.push({
      source: parentId,
      target: '__pending__',
      value: missingObligation,
    })
  }

  return { nodes, links }
}

/**
 * Transform to grouped Sankey data by award type - pure function
 * Uses flow value map to ensure nodes only appear when there's actual flow
 */
function transformToTypeGrouped(
  data: ApiIdvActivityResponse,
  parentPiid: string,
): SankeyData {
  const parentId = data.parent_unique_id
  const flowValueMap = buildFlowValueMap(data.links)

  const getFlowValue = (nodeId: string, fallback?: number | null): number =>
    flowValueMap.get(nodeId) ?? fallback ?? 0

  const parentNode: SankeyNode = {
    id: parentId,
    nodeColor: getCategoryColor('idv'),
    label: parentPiid,
    piid: parentPiid,
    category: 'idv',
    type: null,
    typeDescription: 'Parent IDV',
    recipientName: null,
    recipientHash: null,
    awardingAgency: null,
    totalObligation: data.summary.rollup_total_obligation,
    actionDate: null,
    level: 0,
    isGrandchild: false,
  }

  // Group by type_description using flow values
  const typeGroups = new Map<
    string,
    { nodes: ApiIdvActivityNode[]; total: number; category: string }
  >()

  for (const node of data.nodes) {
    const typeKey = node.type_description || node.category.toUpperCase()
    const flowValue = getFlowValue(node.id, node.total_obligation)
    const existing = typeGroups.get(typeKey)
    if (existing) {
      existing.nodes.push(node)
      existing.total += flowValue
    } else {
      typeGroups.set(typeKey, {
        nodes: [node],
        total: flowValue,
        category: node.category,
      })
    }
  }

  const nodes: SankeyNode[] = [parentNode]
  const links: SankeyLink[] = []

  // Sort by total obligation descending, filter out zero-flow groups
  const sortedGroups = [...typeGroups.entries()]
    .filter(([, group]) => group.total > 0)
    .sort((a, b) => b[1].total - a[1].total)

  for (const [typeName, group] of sortedGroups) {
    const groupId = `__type_${typeName.replace(/\s+/g, '_').toLowerCase()}__`
    nodes.push({
      id: groupId,
      nodeColor: getTypeColor(typeName, group.category),
      label: `${typeName} (${group.nodes.length})`,
      piid: null,
      category: group.category,
      type: group.nodes[0]?.type || null,
      typeDescription: `${group.nodes.length} ${typeName} awards`,
      recipientName: null,
      recipientHash: null,
      awardingAgency: null,
      totalObligation: group.total,
      actionDate: null,
      level: 1,
      isGrandchild: false,
    })
    links.push({
      source: parentId,
      target: groupId,
      value: group.total,
    })
  }

  // Add pending node
  const { missingCount, missingObligation, hasSignificantMissing } =
    getMissingDataInfo(data.summary)
  if (hasSignificantMissing) {
    nodes.push({
      id: '__pending__',
      nodeColor: getCategoryColor('pending'),
      label: `+${missingCount} pending`,
      piid: null,
      category: 'pending',
      type: null,
      typeDescription: null,
      recipientName: null,
      recipientHash: null,
      awardingAgency: null,
      totalObligation: missingObligation,
      actionDate: null,
      level: 1,
      isGrandchild: false,
      missingCount,
    })
    links.push({
      source: parentId,
      target: '__pending__',
      value: missingObligation,
    })
  }

  return { nodes, links }
}

/**
 * Transform to grouped Sankey data by recipient - pure function
 * Uses flow value map to ensure nodes only appear when there's actual flow
 */
function transformToRecipientGrouped(
  data: ApiIdvActivityResponse,
  parentPiid: string,
): SankeyData {
  const parentId = data.parent_unique_id
  const flowValueMap = buildFlowValueMap(data.links)

  const getFlowValue = (nodeId: string, fallback?: number | null): number =>
    flowValueMap.get(nodeId) ?? fallback ?? 0

  const parentNode: SankeyNode = {
    id: parentId,
    nodeColor: getCategoryColor('idv'),
    label: parentPiid,
    piid: parentPiid,
    category: 'idv',
    type: null,
    typeDescription: 'Parent IDV',
    recipientName: null,
    recipientHash: null,
    awardingAgency: null,
    totalObligation: data.summary.rollup_total_obligation,
    actionDate: null,
    level: 0,
    isGrandchild: false,
  }

  // Group by recipient using flow values
  const recipientGroups = new Map<
    string,
    {
      nodes: ApiIdvActivityNode[]
      total: number
      recipientHash: string | null
    }
  >()

  for (const node of data.nodes) {
    const recipientKey = node.recipient_name || 'Unknown Recipient'
    const flowValue = getFlowValue(node.id, node.total_obligation)
    const existing = recipientGroups.get(recipientKey)
    if (existing) {
      existing.nodes.push(node)
      existing.total += flowValue
    } else {
      recipientGroups.set(recipientKey, {
        nodes: [node],
        total: flowValue,
        recipientHash: node.recipient_hash,
      })
    }
  }

  const nodes: SankeyNode[] = [parentNode]
  const links: SankeyLink[] = []

  // Sort by total obligation descending, filter out zero-flow groups, take top 10 + "Others"
  const sortedGroups = [...recipientGroups.entries()]
    .filter(([, group]) => group.total > 0)
    .sort((a, b) => b[1].total - a[1].total)

  const MAX_RECIPIENTS = 10
  const topGroups = sortedGroups.slice(0, MAX_RECIPIENTS)
  const otherGroups = sortedGroups.slice(MAX_RECIPIENTS)

  // Color palette for recipients
  const recipientColors = [
    '#3b82f6', // Blue
    '#10b981', // Emerald
    '#f59e0b', // Amber
    '#ef4444', // Red
    '#8b5cf6', // Violet
    '#ec4899', // Pink
    '#06b6d4', // Cyan
    '#84cc16', // Lime
    '#f97316', // Orange
    '#6366f1', // Indigo
  ]

  for (let i = 0; i < topGroups.length; i++) {
    const [recipientName, group] = topGroups[i]
    const groupId = `__recipient_${i}__`
    const hasMultipleCategories =
      new Set(group.nodes.map((n) => n.category)).size > 1
    nodes.push({
      id: groupId,
      nodeColor: recipientColors[i % recipientColors.length],
      label:
        recipientName.length > 25
          ? recipientName.slice(0, 22) + '...'
          : recipientName,
      piid: null,
      category: hasMultipleCategories
        ? 'mixed'
        : group.nodes[0]?.category || 'contract',
      type: null,
      typeDescription: `${group.nodes.length} awards to ${recipientName}`,
      recipientName: recipientName,
      recipientHash: group.recipientHash,
      awardingAgency: null,
      totalObligation: group.total,
      actionDate: null,
      level: 1,
      isGrandchild: false,
    })
    links.push({
      source: parentId,
      target: groupId,
      value: group.total,
    })
  }

  // Add "Others" group if there are more recipients with flow
  if (otherGroups.length > 0) {
    const othersTotal = otherGroups.reduce((sum, [, g]) => sum + g.total, 0)
    const othersCount = otherGroups.reduce(
      (sum, [, g]) => sum + g.nodes.length,
      0,
    )
    if (othersTotal > 0) {
      nodes.push({
        id: '__recipient_others__',
        nodeColor: '#9ca3af', // Gray
        label: `+${otherGroups.length} others (${othersCount})`,
        piid: null,
        category: 'mixed',
        type: null,
        typeDescription: `${othersCount} awards to ${otherGroups.length} other recipients`,
        recipientName: null,
        recipientHash: null,
        awardingAgency: null,
        totalObligation: othersTotal,
        actionDate: null,
        level: 1,
        isGrandchild: false,
      })
      links.push({
        source: parentId,
        target: '__recipient_others__',
        value: othersTotal,
      })
    }
  }

  // Add pending node
  const { missingCount, missingObligation, hasSignificantMissing } =
    getMissingDataInfo(data.summary)
  if (hasSignificantMissing) {
    nodes.push({
      id: '__pending__',
      nodeColor: getCategoryColor('pending'),
      label: `+${missingCount} pending`,
      piid: null,
      category: 'pending',
      type: null,
      typeDescription: null,
      recipientName: null,
      recipientHash: null,
      awardingAgency: null,
      totalObligation: missingObligation,
      actionDate: null,
      level: 1,
      isGrandchild: false,
      missingCount,
    })
    links.push({
      source: parentId,
      target: '__pending__',
      value: missingObligation,
    })
  }

  return { nodes, links }
}

/**
 * Build a map of node ID to its incoming flow value from API links
 * Pure function - creates new Map without mutations
 */
function buildFlowValueMap(
  apiLinks: ApiIdvActivityLink[],
): Map<string, number> {
  const map = new Map<string, number>()
  for (const link of apiLinks) {
    if (typeof link.value === 'number' && link.value > 0) {
      // Sum values if there are multiple links to the same target
      const existing = map.get(link.target) ?? 0
      map.set(link.target, existing + link.value)
    }
  }
  return map
}

/**
 * Create a SankeyNode from an API node - pure factory function
 */
function createSankeyNode(
  apiNode: ApiIdvActivityNode,
  flowValue: number,
  level: number,
  colorFn: (type: string | null, category: string) => string,
): SankeyNode {
  return {
    id: apiNode.id,
    nodeColor: colorFn(apiNode.type_description, apiNode.category),
    label: apiNode.piid ?? apiNode.id.slice(-8),
    piid: apiNode.piid,
    category: apiNode.category,
    type: apiNode.type,
    typeDescription: apiNode.type_description,
    recipientName: apiNode.recipient_name,
    recipientHash: apiNode.recipient_hash,
    awardingAgency: apiNode.awarding_agency,
    totalObligation: flowValue,
    actionDate: apiNode.action_date,
    level,
    isGrandchild: apiNode.is_grandchild,
  }
}

/**
 * Create a category node - pure factory function
 */
function createCategoryNode(
  id: string,
  label: string,
  category: string,
  description: string,
  level: number,
  color: string,
  totalObligation: number,
  isGrandchild = false,
): SankeyNode {
  return {
    id,
    nodeColor: color,
    label,
    piid: null,
    category,
    type: null,
    typeDescription: description,
    recipientName: null,
    recipientHash: null,
    awardingAgency: null,
    totalObligation,
    actionDate: null,
    level,
    isGrandchild,
  }
}

/**
 * Transform to hierarchy view: Parent → Category Nodes → Individual Items
 * Shows: IDV → Child IDVs → individual IDVs
 *             → Orders → individual orders
 *
 * Pure function - same inputs always produce same outputs
 * Uses API link values for flow amounts (more reliable than node.total_obligation)
 */
function transformToHierarchyGrouped(
  data: ApiIdvActivityResponse,
  parentPiid: string,
): SankeyData {
  const parentId = data.parent_unique_id

  // Build flow value map from API links - this contains the actual flow values
  const flowValueMap = buildFlowValueMap(data.links)

  // Helper to get flow value for a node, falling back to total_obligation
  const getFlowValue = (nodeId: string, fallback?: number | null): number =>
    flowValueMap.get(nodeId) ?? fallback ?? 0

  // Create parent node
  const parentNode: SankeyNode = {
    id: parentId,
    nodeColor: getCategoryColor('idv'),
    label: parentPiid,
    piid: parentPiid,
    category: 'idv',
    type: null,
    typeDescription: 'Parent IDV',
    recipientName: null,
    recipientHash: null,
    awardingAgency: null,
    totalObligation: data.summary.rollup_total_obligation,
    actionDate: null,
    level: 0,
    isGrandchild: false,
  }

  const nodes: SankeyNode[] = [parentNode]
  const links: SankeyLink[] = []

  // Partition nodes by category - pure filter operations
  const idvNodes = data.nodes.filter(
    (n) => n.category === 'idv' && !n.is_grandchild,
  )
  const directContractNodes = data.nodes.filter(
    (n) => n.category !== 'idv' && !n.is_grandchild,
  )
  const grandchildNodes = data.nodes.filter((n) => n.is_grandchild)

  // Process Child IDVs - calculate flow first, only add nodes if there's actual flow
  if (idvNodes.length > 0) {
    const categoryId = '__cat_idv__'

    // First pass: collect nodes with flow and calculate total
    const idvNodesWithFlow: Array<{
      node: SankeyNode
      apiNode: ApiIdvActivityNode
      flowValue: number
    }> = []
    let categoryTotal = 0

    for (const apiNode of idvNodes) {
      const flowValue = getFlowValue(apiNode.id, apiNode.total_obligation)
      if (flowValue > 0) {
        idvNodesWithFlow.push({
          node: createSankeyNode(apiNode, flowValue, 2, getTypeColor),
          apiNode,
          flowValue,
        })
        categoryTotal += flowValue
      }
    }

    // Only add category and items if there's actual flow
    if (categoryTotal > 0) {
      nodes.push(
        createCategoryNode(
          categoryId,
          'Child IDVs',
          'idv',
          `${idvNodesWithFlow.length} child IDVs`,
          1,
          getCategoryColor('idv'),
          categoryTotal,
        ),
      )

      for (const { node, flowValue } of idvNodesWithFlow) {
        nodes.push(node)
        links.push({ source: categoryId, target: node.id, value: flowValue })
      }

      links.push({ source: parentId, target: categoryId, value: categoryTotal })
    }
  }

  // Process Direct Orders - calculate flow first, only add nodes if there's actual flow
  if (directContractNodes.length > 0) {
    const categoryId = '__cat_order__'

    // First pass: collect nodes with flow and calculate total
    const contractNodesWithFlow: Array<{
      node: SankeyNode
      apiNode: ApiIdvActivityNode
      flowValue: number
    }> = []
    let categoryTotal = 0

    for (const apiNode of directContractNodes) {
      const flowValue = getFlowValue(apiNode.id, apiNode.total_obligation)
      if (flowValue > 0) {
        contractNodesWithFlow.push({
          node: createSankeyNode(apiNode, flowValue, 2, getTypeColor),
          apiNode,
          flowValue,
        })
        categoryTotal += flowValue
      }
    }

    // Only add category and items if there's actual flow
    if (categoryTotal > 0) {
      nodes.push(
        createCategoryNode(
          categoryId,
          'Direct Orders',
          'contract',
          `${contractNodesWithFlow.length} direct orders`,
          1,
          getCategoryColor('contract'),
          categoryTotal,
        ),
      )

      for (const { node, flowValue } of contractNodesWithFlow) {
        nodes.push(node)
        links.push({ source: categoryId, target: node.id, value: flowValue })
      }

      links.push({ source: parentId, target: categoryId, value: categoryTotal })
    }
  }

  // Process Grandchild Orders - calculate flow first, only add nodes if there's actual flow
  if (grandchildNodes.length > 0 && idvNodes.length > 0) {
    const categoryId = '__cat_grandchild__'
    const parentCategoryId = '__cat_idv__'

    // First pass: collect nodes with flow and calculate total
    const grandchildNodesWithFlow: Array<{
      node: SankeyNode
      apiNode: ApiIdvActivityNode
      flowValue: number
    }> = []
    let categoryTotal = 0

    for (const apiNode of grandchildNodes) {
      const flowValue = getFlowValue(apiNode.id, apiNode.total_obligation)
      if (flowValue > 0) {
        grandchildNodesWithFlow.push({
          node: {
            ...createSankeyNode(apiNode, flowValue, 3, () => '#6ee7b7'),
            isGrandchild: true,
          },
          apiNode,
          flowValue,
        })
        categoryTotal += flowValue
      }
    }

    // Only add category and items if there's actual flow AND the parent category exists
    const parentCategoryExists = nodes.some((n) => n.id === parentCategoryId)
    if (categoryTotal > 0 && parentCategoryExists) {
      nodes.push(
        createCategoryNode(
          categoryId,
          'Grandchild Orders',
          'contract',
          `${grandchildNodesWithFlow.length} orders under child IDVs`,
          2,
          '#22d3d1',
          categoryTotal,
          true,
        ),
      )

      for (const { node, flowValue } of grandchildNodesWithFlow) {
        nodes.push(node)
        links.push({ source: categoryId, target: node.id, value: flowValue })
      }

      links.push({
        source: parentCategoryId,
        target: categoryId,
        value: categoryTotal,
      })
    }
  }

  // Add pending node
  const { missingCount, missingObligation, hasSignificantMissing } =
    getMissingDataInfo(data.summary)
  if (hasSignificantMissing) {
    nodes.push({
      id: '__pending__',
      nodeColor: getCategoryColor('pending'),
      label: `+${missingCount} pending`,
      piid: null,
      category: 'pending',
      type: null,
      typeDescription: null,
      recipientName: null,
      recipientHash: null,
      awardingAgency: null,
      totalObligation: missingObligation,
      actionDate: null,
      level: 1,
      isGrandchild: false,
      missingCount,
    })
    links.push({
      source: data.parent_unique_id,
      target: '__pending__',
      value: missingObligation,
    })
  }

  return { nodes, links }
}

/**
 * Transform data based on grouping mode - pure function
 */
function transformWithGrouping(
  data: ApiIdvActivityResponse,
  parentPiid: string,
  grouping: GroupingMode,
): SankeyData {
  switch (grouping) {
    case 'hierarchy':
      return transformToHierarchyGrouped(data, parentPiid)
    case 'category':
      return transformToCategoryGrouped(data, parentPiid)
    case 'type':
      return transformToTypeGrouped(data, parentPiid)
    case 'recipient':
      return transformToRecipientGrouped(data, parentPiid)
    case 'individual':
    default:
      return transformToSankeyData(data, parentPiid)
  }
}

// ============================================================================
// Pure Presentation Components
// ============================================================================

/** Tooltip detail row - pure */
function TooltipRow({
  label,
  value,
  highlight = false,
}: {
  label: string
  value: string | React.ReactNode
  highlight?: boolean
}) {
  return (
    <div className="flex justify-between gap-4 text-sm">
      <span className="text-primary-500 dark:text-primary-200">{label}</span>
      <span
        className={
          highlight
            ? 'font-semibold text-primary-900 dark:text-primary-100'
            : 'text-primary-700 dark:text-primary-300'
        }
      >
        {value}
      </span>
    </div>
  )
}

/** Category badge - pure */
function CategoryBadge({
  category,
  isGrandchild,
}: {
  category: string
  isGrandchild: boolean
}) {
  const color = getCategoryColor(category)
  const label = getCategoryLabel(category, isGrandchild)
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium text-white"
      style={{ backgroundColor: color }}
    >
      {label}
    </span>
  )
}

/**
 * Custom tooltip component for Sankey nodes - pure
 */
function NodeTooltip({
  node,
  isClickable,
}: {
  node: SankeyNode
  isClickable: boolean
}) {
  if (node.category === 'pending') {
    return (
      <div className="max-w-xs rounded-lg bg-white p-3 shadow-xl ring-1 ring-primary-200 dark:bg-primary-800 dark:ring-primary-700">
        <div className="mb-2 flex items-center gap-2">
          <CategoryBadge category="pending" isGrandchild={false} />
          <span className="font-medium text-primary-900 dark:text-primary-100">
            {node.missingCount} Additional Orders
          </span>
        </div>
        <div className="mb-2 text-sm text-primary-600 dark:text-primary-200">
          {formatCompactCurrencyOrUnknown(node.totalObligation)} in obligations
          from orders not yet in our database.
        </div>
        <div className="text-xs text-primary-500 dark:text-primary-200">
          USASpending pre-aggregates these totals. As we continue ingesting
          data, more orders will appear here.
        </div>
      </div>
    )
  }

  return (
    <div className="min-w-64 max-w-sm rounded-lg bg-white p-3 shadow-xl ring-1 ring-primary-200 dark:bg-primary-800 dark:ring-primary-700">
      {/* Header */}
      <div className="mb-2 flex items-start justify-between gap-2">
        <div>
          <div className="font-semibold text-primary-900 dark:text-primary-100">
            {node.piid ?? node.label}
          </div>
          {node.typeDescription && (
            <div className="text-xs text-primary-500 dark:text-primary-200">
              {node.typeDescription}
            </div>
          )}
        </div>
        <CategoryBadge
          category={node.category}
          isGrandchild={node.isGrandchild}
        />
      </div>

      {/* Recipient */}
      {node.recipientName && (
        <div className="mb-2 border-b border-primary-100 pb-2 dark:border-primary-700">
          <div className="text-sm font-medium text-primary-800 dark:text-primary-200">
            {node.recipientName}
          </div>
        </div>
      )}

      {/* Details */}
      <div className="space-y-1">
        <TooltipRow
          label="Obligation"
          value={formatCompactCurrencyOrUnknown(node.totalObligation)}
          highlight
        />
        {node.awardingAgency && (
          <TooltipRow label="Agency" value={node.awardingAgency} />
        )}
        {node.actionDate && (
          <TooltipRow label="Action Date" value={formatDate(node.actionDate)} />
        )}
      </div>

      {/* Click hint */}
      {isClickable && (
        <div className="mt-2 flex items-center gap-1 border-t border-primary-100 pt-2 text-xs font-medium text-accent-600 dark:border-primary-700 dark:text-accent-400">
          <span>Click to view details</span>
          <span>→</span>
        </div>
      )}
    </div>
  )
}

/**
 * Custom tooltip component for Sankey links - pure
 */
function LinkTooltip({
  sourceNode,
  targetNode,
  value,
}: {
  sourceNode: SankeyNode
  targetNode: SankeyNode
  value: number
}) {
  return (
    <div className="rounded-lg bg-white p-3 shadow-xl ring-1 ring-primary-200 dark:bg-primary-800 dark:ring-primary-700">
      <div className="mb-2 text-sm font-medium text-primary-900 dark:text-primary-100">
        {sourceNode.piid ?? sourceNode.label} →{' '}
        {targetNode.piid ?? targetNode.label}
      </div>
      <TooltipRow
        label="Flow Amount"
        value={formatCompactCurrency(value)}
        highlight
      />
      {targetNode.recipientName && (
        <div className="mt-2 text-xs text-primary-500 dark:text-primary-200">
          To: {targetNode.recipientName}
        </div>
      )}
    </div>
  )
}

/**
 * Empty state when no activity data - pure
 */
function EmptyState() {
  return (
    <div className="flex h-64 items-center justify-center rounded-lg border border-dashed border-primary-200 bg-primary-50 dark:border-primary-700 dark:bg-primary-800/50">
      <div className="text-center">
        <div className="text-sm text-primary-500 dark:text-primary-200">
          No child or grandchild awards found for this IDV.
        </div>
      </div>
    </div>
  )
}

/**
 * Grouping mode selector - pure presentation component
 */
function GroupingSelector({
  value,
  onChange,
}: {
  value: GroupingMode
  onChange: (mode: GroupingMode) => void
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs font-medium text-primary-500 dark:text-primary-200">
        Group by:
      </span>
      <div className="inline-flex rounded-lg bg-primary-100 p-0.5 dark:bg-primary-800">
        {GROUPING_OPTIONS.map((option) => (
          <button
            key={option.value}
            onClick={() => onChange(option.value)}
            className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
              value === option.value
                ? 'bg-white text-primary-900 shadow-sm dark:bg-primary-700 dark:text-primary-100'
                : 'text-primary-600 hover:text-primary-900 dark:text-primary-200 dark:hover:text-primary-200'
            }`}
            title={option.description}
            type="button"
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  )
}

// ============================================================================
// Headless Hook - Extracts stateful logic
// ============================================================================

interface UseIdvActivitySankeyOptions {
  data: ApiIdvActivityResponse
  parentPiid: string
  /** Controlled grouping mode */
  grouping?: GroupingMode
  /** Default grouping for uncontrolled mode */
  defaultGrouping?: GroupingMode
  /** Callback when grouping changes */
  onGroupingChange?: (mode: GroupingMode) => void
  /** Enable click navigation */
  enableNavigation?: boolean
}

interface UseIdvActivitySankeyReturn {
  sankeyData: SankeyData
  nodeMap: Map<string, SankeyNode>
  grouping: GroupingMode
  setGrouping: (mode: GroupingMode) => void
  handleNavigation: (nodeId: string) => void
  handleLinkClick: (link: SankeyLinkDatum<SankeyNode, SankeyLink>) => void
  isEmpty: boolean
}

/**
 * Headless hook for IDV Activity Sankey logic
 * Supports both controlled and uncontrolled grouping mode
 */
function useIdvActivitySankey({
  data,
  parentPiid,
  grouping: controlledGrouping,
  defaultGrouping = 'hierarchy',
  onGroupingChange,
  enableNavigation = true,
}: UseIdvActivitySankeyOptions): UseIdvActivitySankeyReturn {
  const router = useRouter()

  // Support controlled/uncontrolled pattern for grouping
  const [internalGrouping, setInternalGrouping] =
    useState<GroupingMode>(defaultGrouping)
  const isControlled = controlledGrouping !== undefined
  const grouping = isControlled ? controlledGrouping : internalGrouping

  const setGrouping = useCallback(
    (mode: GroupingMode) => {
      if (!isControlled) {
        setInternalGrouping(mode)
      }
      onGroupingChange?.(mode)
    },
    [isControlled, onGroupingChange],
  )

  // Transform data based on grouping mode - pure transformation
  const sankeyData = useMemo(
    () => transformWithGrouping(data, parentPiid, grouping),
    [data, parentPiid, grouping],
  )

  // Build node lookup map - pure transformation
  const nodeMap = useMemo(
    () => buildNodeMap(sankeyData.nodes),
    [sankeyData.nodes],
  )

  // Navigation handler
  const handleNavigation = useCallback(
    (nodeId: string) => {
      if (!enableNavigation) return

      const node = nodeMap.get(nodeId)
      if (!node) return

      // Don't navigate for grouped nodes, pending, or parent
      if (nodeId.startsWith('__')) return
      if (node.category === 'pending') return
      if (node.level === 0) return

      router.push(`/awards/usaspending/${encodeURIComponent(nodeId)}`)
    },
    [enableNavigation, nodeMap, router],
  )

  // Link click handler
  const handleLinkClick = useCallback(
    (link: SankeyLinkDatum<SankeyNode, SankeyLink>) => {
      const targetId =
        typeof link.target === 'string' ? link.target : link.target.id
      handleNavigation(targetId)
    },
    [handleNavigation],
  )

  const isEmpty = sankeyData.links.length === 0

  return {
    sankeyData,
    nodeMap,
    grouping,
    setGrouping,
    handleNavigation,
    handleLinkClick,
    isEmpty,
  }
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * IDV Activity Sankey Visualization
 *
 * Interactive Sankey diagram showing money flow from parent IDV
 * to child awards and grandchild awards.
 *
 * Features:
 * - Multiple grouping modes (Individual, Category, Type, Recipient)
 * - Click on nodes/links to navigate to award details
 * - Rich hover tooltips with detailed information
 * - Supports controlled and uncontrolled grouping mode
 * - Smooth animations and visual feedback
 */
export function IdvActivitySankey({
  data,
  parentPiid,
  onNodeClick,
  height = 400,
  enableNavigation = true,
  grouping: controlledGrouping,
  defaultGrouping = 'hierarchy',
  onGroupingChange,
  showGroupingControls = true,
}: IdvActivitySankeyProps) {
  // Theme detection for dark mode styling. Asked positively: every theme but
  // one is light, so "not light" would paint pale labels onto a pale chart.
  const { resolvedTheme } = useTheme()
  const isMounted = useMounted()
  const isDark = isMounted && resolvedTheme === APP_THEME.dark

  // Nivo theme for dark mode label colors
  const nivoTheme = useMemo(
    () =>
      isDark
        ? {
            labels: {
              text: {
                fill: '#e5e7eb', // Light gray for dark mode
              },
            },
          }
        : undefined,
    [isDark],
  )

  // Use headless hook for all stateful logic
  const {
    sankeyData,
    nodeMap,
    grouping,
    setGrouping,
    handleNavigation,
    handleLinkClick,
    isEmpty,
  } = useIdvActivitySankey({
    data,
    parentPiid,
    grouping: controlledGrouping,
    defaultGrouping,
    onGroupingChange,
    enableNavigation,
  })

  // Wrap navigation to also call onNodeClick
  const handleNodeNavigation = useCallback(
    (nodeId: string) => {
      onNodeClick?.(nodeId)
      handleNavigation(nodeId)
    },
    [onNodeClick, handleNavigation],
  )

  // Check if a node is clickable (individual award, not a group/category/parent)
  const isNodeClickable = useCallback(
    (node: SankeyNode) => {
      if (!enableNavigation) return false
      if (node.id.startsWith('__')) return false // Group/category nodes
      if (node.category === 'pending') return false
      if (node.level === 0) return false // Parent node
      return true
    },
    [enableNavigation],
  )

  // Nivo callback types omit 'color'/'label', but our data has these properties
  type NivoNodeDatum = Omit<
    SankeyNodeDatum<SankeyNode, SankeyLink>,
    'color' | 'label'
  >

  // Stable callback for node colors
  const getNodeColor = useCallback((node: NivoNodeDatum) => node.nodeColor, [])

  // Stable callback for node labels - cast to access our original label
  const getNodeLabel = useCallback(
    (node: NivoNodeDatum) => (node as unknown as SankeyNode).label,
    [],
  )

  // Stable callback for node tooltips
  const renderNodeTooltip = useCallback(
    ({ node }: { node: SankeyNode }) => (
      <NodeTooltip node={node} isClickable={isNodeClickable(node)} />
    ),
    [isNodeClickable],
  )

  // Stable callback for link tooltips
  const renderLinkTooltip = useCallback(
    ({ link }: { link: SankeyLinkDatum<SankeyNode, SankeyLink> }) => {
      const sourceNode =
        typeof link.source === 'string' ? nodeMap.get(link.source) : link.source
      const targetNode =
        typeof link.target === 'string' ? nodeMap.get(link.target) : link.target

      if (!sourceNode || !targetNode) return null

      return (
        <LinkTooltip
          sourceNode={sourceNode}
          targetNode={targetNode}
          value={link.value}
        />
      )
    },
    [nodeMap],
  )

  // Stable click handler for nodes
  const handleClick = useCallback(
    (data: SankeyNode | SankeyLinkDatum<SankeyNode, SankeyLink>) => {
      // Discriminate between node and link by checking for 'source' property
      if ('source' in data && 'target' in data) {
        // It's a link
        handleLinkClick(data as SankeyLinkDatum<SankeyNode, SankeyLink>)
      } else {
        // It's a node
        const node = data as SankeyNode
        if (isNodeClickable(node)) {
          handleNodeNavigation(node.id)
        }
      }
    },
    [handleLinkClick, handleNodeNavigation, isNodeClickable],
  )

  if (isEmpty) {
    return <EmptyState />
  }

  // Calculate chart height (subtract controls height if showing)
  const chartHeight = showGroupingControls ? height - 40 : height

  return (
    <div className="flex flex-col">
      {/* Grouping Controls */}
      {showGroupingControls && (
        <div className="mb-2 flex justify-end">
          <GroupingSelector value={grouping} onChange={setGrouping} />
        </div>
      )}

      {/* Sankey Chart */}
      <div style={{ height: chartHeight }} className="sankey-container w-full">
        <style jsx>{`
          /* Wider hit area for link hover */
          .sankey-container :global(path[fill^='url']),
          .sankey-container :global(path[fill-opacity]) {
            stroke: transparent;
            stroke-width: 20px;
          }
          /* Force light text color for labels in dark mode */
          :global(.dark) .sankey-container :global(text) {
            fill: #e5e7eb !important;
          }
        `}</style>
        <ResponsiveSankey
          data={sankeyData}
          theme={nivoTheme}
          margin={{ top: 20, right: 180, bottom: 20, left: 180 }}
          align="justify"
          sort="input"
          colors={getNodeColor}
          nodeOpacity={1}
          nodeHoverOpacity={1}
          nodeHoverOthersOpacity={0.35}
          nodeThickness={20}
          nodeSpacing={28}
          nodeBorderWidth={0}
          nodeBorderColor={{ from: 'color', modifiers: [['darker', 0.8]] }}
          nodeBorderRadius={4}
          linkOpacity={isDark ? 0.7 : 0.5}
          linkHoverOpacity={0.8}
          linkHoverOthersOpacity={isDark ? 0.2 : 0.1}
          linkBlendMode={isDark ? 'normal' : 'multiply'}
          linkContract={0}
          enableLinkGradient
          label={getNodeLabel}
          labelPosition="outside"
          labelOrientation="horizontal"
          labelPadding={16}
          labelTextColor={
            isDark ? '#e5e7eb' : { from: 'color', modifiers: [['darker', 1.2]] }
          }
          animate
          motionConfig="gentle"
          nodeTooltip={renderNodeTooltip}
          linkTooltip={renderLinkTooltip}
          onClick={handleClick}
        />
      </div>
    </div>
  )
}

/**
 * Type guard to check if activity data has visualizable content
 */
export function hasActivityData(
  data: ApiIdvActivityResponse | undefined,
): data is ApiIdvActivityResponse {
  return data !== undefined && (data.nodes.length > 0 || data.links.length > 0)
}

// Export types and utilities for consumers
export type { GroupingMode }
export { GROUPING_OPTIONS, getMissingDataInfo }
